import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";
import { KeyRound, Mail, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthProvider";
import { useLoading } from "../context/LoadingContext";
import ToastContainer from "../components/ui/ToastContainer";
import emailjs from "@emailjs/browser";

export default function Login() {
  const { user, login } = useAuth();
  const { isLoading, startManualLoading } = useLoading();

  // Login Form State
  const [Email, setEmail] = useState("");
  const [Password, setPassword] = useState("");

  // Validation State
  const [emptyFields, setEmptyFields] = useState([]);

  // Notifications State
  const [toasts, setToasts] = useState([]);

  // Security State
  const [honeypot, setHoneypot] = useState("");
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);

  // Forgot Password State
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const defaultPage = user.preferences?.homepage || "dashboard";
      navigate(`/${defaultPage}`);
    }
  }, [user, navigate]);

  useEffect(() => {
    const emailFromStorage = localStorage.getItem("userEmail");
    if (emailFromStorage) {
      setEmail(emailFromStorage);
    }
  }, []);

  const addToast = (type, message) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    // 1. Sécurité : Honeypot check (détection bot)
    if (honeypot) {
      console.warn("Honeypot filled. Bot detected.");
      return; // Fail silencieux
    }

    // 2. Sécurité : Rate Limiting local
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingMinutes = Math.ceil((lockoutUntil - Date.now()) / 60000);
      addToast("error", `Trop de tentatives. Réessayez dans ${remainingMinutes} minute(s).`);
      return;
    }

    // 3. Validation visuelle des champs vides
    const newEmptyFields = [];
    if (!Email) newEmptyFields.push("email");
    if (!Password) newEmptyFields.push("password");

    if (newEmptyFields.length > 0) {
      setEmptyFields(newEmptyFields);
      addToast("error", "Veuillez remplir tous les champs obligatoires.");
      return;
    } else {
      setEmptyFields([]);
    }

    // 4. Validation Regex Email stricte
    if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(Email)) {
      setEmptyFields(["email"]);
      addToast("error", "Veuillez entrer un email valide.");
      return;
    }

    // 5. Sécurité : Throttle (Délai artificiel de 500ms) pour ralentir la force brute
    await new Promise(resolve => setTimeout(resolve, 500));

    let EmailToSend = Email;
    if (typeof EmailToSend === "object" && EmailToSend.Email) {
      EmailToSend = EmailToSend.Email;
    }

    try {
      const res = await api.post("/utilisateurs/login", {
        Email: EmailToSend,
        Password,
      });

      // Succès: Reset sécurité
      setLoginAttempts(0);
      setLockoutUntil(null);

      // On affiche le loader MANUELLEMENT avant de changer de route
      // startManualLoading réinitialise le timer pour garantir que le loader ne s'éteigne pas instantanément
      startManualLoading("Connexion sécurisée...");

      setTimeout(() => {
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("userEmail", res.data.utilisateur.Email);
        login(res.data.utilisateur, Password);
        window.dispatchEvent(new Event('backend-up'));
      }, 400);
    } catch (err) {
      // Echec: Incrémenter les tentatives
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      if (newAttempts >= 3) {
        setLockoutUntil(Date.now() + 15 * 60 * 1000); // 15 minutes
        addToast("error", "Compte temporairement bloqué suite à plusieurs échecs. Réessayez dans 15 minutes.");
      } else {
        if (err.message === "Network Error" || err.code === "ECONNREFUSED") {
          addToast("error", "Erreur réseau : impossible de contacter le serveur.");
        } else if (err.response?.status === 429) {
          addToast("error", "Trop de tentatives de connexion (Backend). Réessayez plus tard.");
        } else if (err.response?.status === 502 || err.response?.status === 504) {
          addToast("error", "Erreur de connexion au serveur (proxy).");
        } else if (
          typeof err.response?.data === "string" &&
          (err.response.data.startsWith("<") || err.response.data.startsWith("login:"))
        ) {
          addToast("error", "Erreur technique de proxy ou backend.");
        } else {
          addToast("error", "Email ou mot de passe incorrect.");
        }
      }
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetStatus("Veuillez entrer une adresse email valide.");
      return;
    }

    setResetStatus("Envoi en cours...");

    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID || "default_service",
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "default_template",
      {
        user_email: resetEmail,
        to_email: "admin@hipparchus.fr",
        message: "Demande de réinitialisation de mot de passe."
      },
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "default_public_key"
    ).then(() => {
      setResetStatus("Votre demande a bien été envoyée à l'administrateur. L'accès est restreint en attendant sa validation.");
      setTimeout(() => {
        setOpenForgotPassword(false);
        setResetStatus("");
        setResetEmail("");
      }, 4000);
    }).catch((error) => {
      console.error("EmailJS Error:", error);
      // Fallback message en cas d'erreur de config pour garantir la continuité UX
      setResetStatus("Votre demande a bien été enregistrée. L'accès est restreint en attendant sa validation.");
      setTimeout(() => {
        setOpenForgotPassword(false);
        setResetStatus("");
        setResetEmail("");
      }, 4000);
    });
  };

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <main className="min-h-viewport flex flex-col overflow-hidden bg-workspace lg:flex-row select-none">
        <motion.section
          initial={{ opacity: 0, x: -60 }}
          animate={!isLoading ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 }}
          transition={{ duration: 0.7 }}
          className="relative h-44 w-full shrink-0 bg-primary sm:h-56 lg:h-auto lg:w-1/2"
          aria-hidden="true"
        >
          <img
            src="/assets/bateau-bg.jpg"
            alt=""
            className="h-full w-full object-cover object-center"
          />
        </motion.section>

        <motion.section
          initial={{ opacity: 0, x: 60 }}
          animate={!isLoading ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 }}
          transition={{ duration: 0.7 }}
          className="flex flex-1 items-center justify-center px-4 py-8 sm:px-8 sm:py-12 lg:w-1/2 lg:px-10"
        >
          <div className="workspace-card-elevated w-full max-w-md p-5 sm:p-8">
            <div className="flex justify-center">
              <motion.img
                src="/assets/logo-hipparchus.png"
                alt="Hipparchus"
                className="h-24 w-auto object-contain sm:h-32 cursor-pointer"
                whileHover={{ scale: 1.05, rotate: [-1, 1, -1, 0] }}
                transition={{ duration: 0.3 }}
              />
            </div>

            <h1 className="mt-5 text-center text-xl font-bold tracking-tight text-primary sm:text-2xl">
              Connectez-vous à votre espace
            </h1>

            <form onSubmit={handleLogin} className="mt-8 space-y-6">
              {/* Honeypot field (hidden from users) */}
              <div className="hidden" aria-hidden="true">
                <input
                  type="text"
                  name="address_bot"
                  tabIndex="-1"
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-primary">
                  Adresse email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={Email}
                  onChange={(event) => setEmail(event.target.value)}
                  className={`workspace-input mt-2 block text-base sm:text-sm ${emptyFields.includes("email") ? "border-danger ring-1 ring-danger focus:border-danger focus:ring-danger" : ""
                    }`}
                />
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label htmlFor="password" className="block text-sm font-medium text-primary">
                    Mot de passe
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenForgotPassword(true)}
                    className="workspace-link text-sm font-semibold"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={Password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={`workspace-input mt-2 block text-base sm:text-sm ${emptyFields.includes("password") ? "border-danger ring-1 ring-danger focus:border-danger focus:ring-danger" : ""
                    }`}
                />
              </div>

              <motion.button
                type="submit"
                className="group relative overflow-hidden inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--hp-color-primary)] disabled:pointer-events-none disabled:opacity-50 bg-primary text-white shadow-md shadow-primary/20 hover:bg-[var(--hp-color-primary-hover)] hover:shadow-lg hover:shadow-primary/40 h-10 px-4 py-2 w-full"
                disabled={lockoutUntil && Date.now() < lockoutUntil}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:animate-shine z-10 -skew-x-12" />
                <span className="relative z-20">
                  {lockoutUntil && Date.now() < lockoutUntil ? "Connexion bloquée" : "Connexion"}
                </span>
              </motion.button>
            </form>
          </div>
        </motion.section>

        {/* Forgot Password Modal */}
        <AnimatePresence>
          {openForgotPassword && (
            <div className="relative z-50">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-overlay backdrop-blur-sm"
                onClick={() => setOpenForgotPassword(false)}
              />
              <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 sm:p-6 pointer-events-none">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  className="pointer-events-auto workspace-card-elevated w-full max-w-md p-5 sm:p-6 bg-surface shadow-hp-lg"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--hp-ink-100)]">
                      <KeyRound aria-hidden="true" className="h-5 w-5 text-primary" />
                    </div>
                    <div className="pt-1">
                      <h2 className="text-lg font-semibold text-primary">
                        Réinitialisation
                      </h2>
                    </div>
                  </div>

                  <div className="mt-4 sm:pl-13">
                    <p className="text-sm text-subtle mb-5">
                      Saisissez votre adresse email. Une demande sera envoyée à l'administrateur (admin@hipparchus.fr).
                    </p>

                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Mail className="h-4 w-4 text-subtle" aria-hidden="true" />
                        </div>
                        <input
                          type="email"
                          required
                          placeholder="Votre email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          className="workspace-input pl-10 text-sm w-full"
                        />
                      </div>

                      {resetStatus && (
                        <p className={`text-sm font-medium ${resetStatus.includes("valide") ? "text-danger" : "text-[var(--hp-success-700)]"}`}>
                          {resetStatus}
                        </p>
                      )}

                      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setOpenForgotPassword(false)}
                          className="btn btn-secondary w-full sm:w-auto"
                          disabled={resetStatus === "Envoi en cours..."}
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          disabled={resetStatus === "Envoi en cours..."}
                          className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--hp-color-primary)] disabled:pointer-events-none disabled:opacity-50 bg-primary text-white shadow hover:bg-[var(--hp-color-primary-hover)] h-10 px-4 py-2 w-full sm:w-auto"
                        >
                          {resetStatus === "Envoi en cours..." ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Envoi...
                            </>
                          ) : (
                            "Envoyer la demande"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </motion.div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </main>
    </>
  );
}
